import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

const MIN_NOTES = 10;

/**
 * N31 (QA 2026-08-07). The mark-paid refusal returns a machine token in
 * `message` ('settlement_refused') and the real reason in `code`. This screen
 * rendered `message` only, so a refused AD saw a red banner reading literally
 * "settlement_refused" — no reason, no next step, and no way to tell "this order
 * is cancelled" from "this order is in the wrong currency". The sibling wire
 * screen already branches on `data.code` (WireReconciliation.jsx:215).
 *
 * Keyed on the settlement ledger's own refusal reasons; keep in step with
 * app/services/settlementLedger.service.js.
 */
const REFUSAL_MESSAGES = {
  TERMINAL: "This order is cancelled, expired or failed, so it cannot be settled. The transfer needs to be returned or reassigned.",
  NOT_CONFIRMED: "This order was never confirmed by the investor, so it cannot be settled.",
  CURRENCY_MISMATCH: "This order is denominated in a different currency from its campaign and cannot be settled. Escalate — it was created before the denomination gate.",
  CURRENCY_UNDECLARED: "This campaign has no declared currency, so money cannot be settled into it. An administrator must set the campaign currency first.",
  ALREADY_SETTLED: "This order has already been settled.",
  NOT_FOUND: "This order no longer exists and cannot be settled.",
};

/** A refusal in words, or null when the error is not a coded refusal. */
function refusalText(data) {
  const code = data && data.code;
  if (!code) return null;
  return REFUSAL_MESSAGES[code]
    // An unrecognised code is still better named than swallowed.
    || `This order could not be settled (${code}). Nothing was changed.`;
}

/**
 * N34 (QA 2026-08-07). The currency health of a row, from the campaign's own
 * currency rather than the order's. Observed on dev: orders against campaign
 * 2505 (currency_type NULL) rendered "10000 CAD" — indistinguishable from a
 * healthy row, on the one screen where a human could have caught it.
 */
function currencyState(r) {
  if (r.currencyUndeclared) return "undeclared";
  if (r.currencyMismatch) return "mismatch";
  return "ok";
}

const dangerTextStyle = { color: "var(--neg-700)", fontWeight: 600 };

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function ManualPaymentConfirm() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [open, setOpen] = useState(null);
  const [grossFee, setGrossFee] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useFocusTrap({ active: !!open, onClose: () => !submitting && setOpen(null) });

  function fetchList() {
    setLoading(true);
    bdcApi
      .adListPendingPayments({ page, limit })
      .then((res) => {
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  function openDialog(row) {
    setOpen(row);
    setGrossFee("");
    setBankRef("");
    setNotes("");
  }

  function closeDialog() {
    if (submitting) return;
    setOpen(null);
  }

  async function submit(ev) {
    ev.preventDefault();
    const grossNum = Number(grossFee);
    if (!Number.isFinite(grossNum) || grossNum < 0) {
      setFlash({ type: "danger", message: "Gross fee must be a non-negative number." });
      return;
    }
    if (notes.trim().length < MIN_NOTES) {
      setFlash({ type: "danger", message: `Notes must be at least ${MIN_NOTES} characters.` });
      return;
    }
    setSubmitting(true);
    try {
      const body = { gross_fee: grossNum, notes: notes.trim() };
      if (bankRef.trim()) body.bank_reference = bankRef.trim();
      const res = await bdcApi.adMarkInvestmentPaid(open.id, body);
      const lines = res?.data?.commission_lines ?? 0;
      setFlash({
        type: "info",
        message: `Investment #${open.id} marked PAID. ${lines} commission line${lines === 1 ? "" : "s"} locked.`,
      });
      setOpen(null);
      fetchList();
    } catch (err) {
      // N31: prefer the coded refusal reason over the server's machine token.
      const data = err?.response?.data;
      setFlash({
        type: "danger",
        message: refusalText(data) || data?.message || err.message || "Mark-paid failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Manual payment confirmation"
        subtitle={<><span className="mono">{total}</span><span>pending · page {page} of {Math.max(totalPages, 1)}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        v1 investors wire funds to Roots' operating bank account. Reconcile the statement, then confirm each investment below. Confirmation writes the payment, locks attribution, and triggers commission-ledger rows in one transaction.
      </p>

      {flash && <Alert tone={flash.type === "ok" ? "info" : flash.type}>{flash.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No investments awaiting payment confirmation.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Inv #</th>
                  <th>User id</th>
                  <th>Campaign</th>
                  <th className="r">Amount</th>
                  <th>Currency</th>
                  <th>Suitability</th>
                  <th>Payment</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-currency-state={currencyState(r)}>
                    <td className="mono">#{r.id}</td>
                    <td className="mono">#{r.user_id}</td>
                    <td className="mono">#{r.campaign_id}</td>
                    <td className="r">{r.amount}</td>
                    {/* N34: the CAMPAIGN's currency, not just the order's. An
                        undenominated campaign can no longer masquerade as CAD. */}
                    <td className="muted">
                      {r.currencyUndeclared ? (
                        <span style={dangerTextStyle}>No campaign currency</span>
                      ) : r.currencyMismatch ? (
                        <span style={dangerTextStyle}>
                          {r.currency || "—"} ≠ {r.payableCurrency}
                        </span>
                      ) : (
                        r.currency || "—"
                      )}
                    </td>
                    <td className="muted">{r.suitability_status || "—"}</td>
                    <td className="muted">{r.paymentStatus || "UNPAID"}</td>
                    <td className="muted">{formatAuditTimestamp(r.createdAt)}</td>
                    <td className="r">
                      <Btn variant="primary" size="sm" onClick={() => openDialog(r)}>Confirm paid</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1 || submitting} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>{page} / {totalPages}</span>
          <Btn size="sm" disabled={page === totalPages || submitting} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {open && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-pay-dialog-title"
          ref={dialogRef}
        >
          <form onSubmit={submit} style={dialogStyle}>
            <h3 id="ad-pay-dialog-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 12px" }}>
              Confirm payment for investment #{open.id}
            </h3>
            {/* N34: warn where the decision is actually made, not only in the
                list. Settlement will REFUSE this order (CURRENCY_UNDECLARED),
                so say so before the AD fills in a fee and a justification. */}
            {open.currencyUndeclared && (
              <Alert tone="danger">
                Campaign #{open.campaign_id} has <strong>no declared currency</strong>, so this
                payment cannot be settled against it. An administrator must set the campaign
                currency first — confirming here will be refused.
              </Alert>
            )}
            {open.currencyMismatch && (
              <Alert tone="danger">
                This order is denominated <strong>{open.currency}</strong> but campaign #{open.campaign_id}
                {" "}is payable in <strong>{open.payableCurrency}</strong>. Settlement will be refused —
                escalate rather than retrying.
              </Alert>
            )}
            <dl style={{ margin: "0 0 16px" }}>
              <KV label="Investor" value={`#${open.user_id}`} />
              <KV label="Campaign" value={`#${open.campaign_id}`} />
              <KV label="Amount" value={`${open.amount} ${open.currency || ""}`} />
              <KV
                label="Campaign currency"
                value={open.currencyUndeclared
                  ? "not declared — cannot be settled"
                  : (open.campaignCurrency || "—")}
              />
              <KV label="Suitability" value={open.suitability_status || "—"} />
              <KV label="Current payment status" value={open.paymentStatus || "UNPAID"} />
            </dl>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                label="Roots gross fee on this transaction (dollars)"
                name="gross_fee"
                type="number"
                step="0.01"
                min="0"
                value={grossFee}
                onChange={(e) => setGrossFee(e.target.value)}
                disabled={submitting}
                autoFocus
                required
              />

              <Field
                label="Bank / wire reference (optional)"
                name="bank_reference"
                type="text"
                maxLength={255}
                placeholder="WT-2026-XXXX"
                value={bankRef}
                onChange={(e) => setBankRef(e.target.value)}
                disabled={submitting}
              />

              <div className="field">
                <label className="field__label" htmlFor="ad-pay-notes">
                  Notes (required, min {MIN_NOTES} chars) — audited justification
                </label>
                <textarea
                  id="ad-pay-notes"
                  className="field__textarea"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                  required
                  minLength={MIN_NOTES}
                />
                <span className="field__hint">{notes.trim().length} / {MIN_NOTES}</span>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={closeDialog} disabled={submitting}>Cancel</Btn>
              <Btn variant="primary" type="submit" disabled={submitting || notes.trim().length < MIN_NOTES}>
                {submitting ? "Confirming…" : "Confirm paid + lock commissions"}
              </Btn>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}

const KV = ({ label, value }) => (
  <>
    <dt className="field__label" style={{ marginTop: 8 }}>{label}</dt>
    <dd style={{ margin: "2px 0 0", fontSize: "var(--t-md)" }}>{value}</dd>
  </>
);

export default ManualPaymentConfirm;
